import { useState, useRef, useEffect, useCallback } from "react";
import { dist, getPos } from "./fdCamEditor/geometry";
import { drawSelection as drawObjectSelection, getBounds, renderObj, drawMagnifierLens, handleTargetPoint, HANDLE_TO_MAG_KEY } from "./fdCamEditor/drawObjects";
import useEditorHistory from "./fdCamEditor/useEditorHistory";
import useCropTool from "./fdCamEditor/useCropTool";
import EditorToolChrome from "./fdCamEditor/EditorToolChrome";
import EditorTopBar from "./fdCamEditor/EditorTopBar";
import SelectionOverlay from "./fdCamEditor/SelectionOverlay";
import TextEditModal from "./fdCamEditor/TextEditModal";
import { drawMultiSelection, getMultiBounds, hitHandle, hitObject, toLocal, localToWorld } from "./fdCamEditor/selection";
import { STICKERS } from "./fdCamEditor/constants";

function scaleAnnotationObjects(objs, fromW, fromH, toW, toH) {
  if (!objs || !objs.length) return [];
  if (!fromW || !fromH || !toW || !toH || (fromW === toW && fromH === toH)) {
    return objs;
  }
  const scaleX = toW / fromW;
  const scaleY = toH / fromH;
  const scaleAvg = (scaleX + scaleY) / 2;

  const scalePt = (p) => (p ? { x: p.x * scaleX, y: p.y * scaleY } : p);

  return objs.map((o) => {
    const no = { ...o };
    if (no.x != null) no.x *= scaleX;
    if (no.y != null) no.y *= scaleY;
    if (no.x1 != null) no.x1 *= scaleX;
    if (no.y1 != null) no.y1 *= scaleY;
    if (no.x2 != null) no.x2 *= scaleX;
    if (no.y2 != null) no.y2 *= scaleY;
    if (no.w != null) no.w *= scaleX;
    if (no.h != null) no.h *= scaleY;

    if (no.vx != null) no.vx *= scaleX;
    if (no.vy != null) no.vy *= scaleY;
    if (no.ax != null) no.ax *= scaleX;
    if (no.ay != null) no.ay *= scaleY;
    if (no.bx != null) no.bx *= scaleX;
    if (no.by != null) no.by *= scaleY;

    if (no.anchorX != null) no.anchorX *= scaleX;
    if (no.anchorY != null) no.anchorY *= scaleY;
    if (no.targetX != null) no.targetX *= scaleX;
    if (no.targetY != null) no.targetY *= scaleY;
    if (no.lensX != null) no.lensX *= scaleX;
    if (no.lensY != null) no.lensY *= scaleY;

    if (Array.isArray(no.points)) no.points = no.points.map(scalePt);
    if (Array.isArray(no.pts)) no.pts = no.pts.map(scalePt);

    if (no.target) no.target = scalePt(no.target);

    if (no.thickness) no.thickness *= scaleAvg;
    if (no.fontSize) no.fontSize *= scaleAvg;
    if (no.lensRadius) no.lensRadius *= scaleAvg;
    if (no.radius) no.radius *= scaleAvg;
    if (no.stickerSize) no.stickerSize *= scaleAvg;

    if (Array.isArray(no.embeddedMagnifiers)) {
      no.embeddedMagnifiers = no.embeddedMagnifiers.map((em) => ({
        ...em,
        lensPos: scalePt(em.lensPos),
        radius: em.radius ? em.radius * scaleAvg : em.radius,
      }));
    }

    return no;
  });
}

export default function FdCamEditor({
  source,
  onSave,
  onClose,
  fileName = "edited-image.png",
  initialAnnotations = null,
  initialObjects = null,
  initialAdjustments = null,
} = {}) {
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const customStickerRef = useRef(null);
  const editorRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imgEl, setImgEl] = useState(null);
  const [imgSize, setImgSize] = useState({ w: 700, h: 500 });
  const [zoom, setZoom] = useState(1);
  const [adjustments, setAdjustments] = useState({ brightness: 100, contrast: 100, saturation: 100 });
  const [saveStatus, setSaveStatus] = useState(null); // null | "saving" | "saved"
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);
  const [currentFileName, setCurrentFileName] = useState(fileName);
  const [sessionRotation, setSessionRotation] = useState(0);
  const savedRotRef = useRef(0);

  // tool state
  const [layoutMode, setLayoutMode] = useState("auto"); // "auto" or "bottom"
  const [tool, setTool] = useState("cursor");
  const [color, setColor] = useState("#ffcc00");
  const [thickness, setThickness] = useState(5);
  const [showColorPopup, setShowColorPopup] = useState(false);
  const [showThickPopup, setShowThickPopup] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [showStickerPanel, setShowStickerPanel] = useState(false);
  const [loadingImage, setLoadingImage] = useState(true);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    setHasDrawn(false);
  }, [tool]);

  // Removed document.body overflow hidden to prevent conflict with Antd Modals

  // text modal
  const [showTextModal, setShowTextModal] = useState(false);
  const [textMode, setTextMode] = useState("abc");
  const [textVal, setTextVal] = useState("");
  const [textFt, setTextFt] = useState(0); const [textIn, setTextIn] = useState(0);
  const [textM, setTextM] = useState(0); const [textCm, setTextCm] = useState(0);
  const [pendingTextPos, setPendingTextPos] = useState(null);
  const [editingObjId, setEditingObjId] = useState(null);
  const [textFontSize, setTextFontSize] = useState(22);
  const [textBold, setTextBold] = useState(true);
  const [textAlign, setTextAlign] = useState("center");
  const [textHighlight, setTextHighlight] = useState(false);
  const [textHighlightColor, setTextHighlightColor] = useState("#ffff00");

  // objects
  const [objects, setObjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]); // multi-select capable
  const [showSelEditor, setShowSelEditor] = useState(false); // T-icon style/text popup
  const idRef = useRef(0);
  const newId = () => ++idRef.current;
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const getDefaultLensPos = (target, radius = 70) => ({
    x: clamp(target.x + radius * 0.95, radius, Math.max(radius, imgSize.w - radius)),
    y: clamp(target.y - radius * 0.95, radius, Math.max(radius, imgSize.h - radius)),
  });

  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showGridOverlay, setShowGridOverlay] = useState(false);
  const [activeMagnifier, setActiveMagnifier] = useState(null); // { objId, handle }
  const magnifierTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (magnifierTimerRef.current) clearTimeout(magnifierTimerRef.current);
    };
  }, []);

  const handleCloseRequest = () => {
    // Check if there are any edits
    const hasChanges = history.length > 0 || objects.length > 0 || 
                       adjustments.brightness !== 100 || 
                       adjustments.contrast !== 100 || 
                       adjustments.saturation !== 100;
    
    if (hasChanges && typeof onClose === "function") {
      setShowCloseConfirm(true);
    } else if (typeof onClose === "function") {
      onClose();
    }
  };

  function getNewHandleCoordinates(sn, handle, dx, dy) {
    if (handle === "p1") return { x: sn.x1 + dx, y: sn.y1 + dy };
    if (handle === "p2") return { x: sn.x2 + dx, y: sn.y2 + dy };
    const rx1 = Math.min(sn.x1, sn.x2), ry1 = Math.min(sn.y1, sn.y2);
    const rx2 = Math.max(sn.x1, sn.x2), ry2 = Math.max(sn.y1, sn.y2);
    let localPt = { x: rx1, y: ry1 };
    if (handle === "tl") localPt = { x: rx1 + dx, y: ry1 + dy };
    else if (handle === "tr") localPt = { x: rx2 + dx, y: ry1 + dy };
    else if (handle === "bl") localPt = { x: rx1 + dx, y: ry2 + dy };
    else if (handle === "br") localPt = { x: rx2 + dx, y: ry2 + dy };
    return localToWorld(sn, localPt);
  }

  function getLensPosition(targetX, targetY, radius = 70) {
    const lensX = clamp(targetX, radius, imgSize.w - radius);
    const isTopSpaceConstrained = targetY - 115 < radius;
    const lensYOffset = isTopSpaceConstrained ? 115 : -115;
    const lensY = clamp(targetY + lensYOffset, radius, imgSize.h - radius);
    return { x: lensX, y: lensY };
  }

  const {
    history,
    redoStack,
    clearHistory,
    pushHist,
    undo,
    redo,
    cloneObj,
    deleteSelected,
    duplicateObjects,
    clipboardCopy,
    clipboardPaste,
  } = useEditorHistory({
    objects,
    setObjects,
    selectedIds,
    setSelectedIds,
    setShowSelEditor,
    newId,
  });

  const loadImageSource = useCallback((src, nextFileName = fileName) => {
    if (!src) {
      setImgEl(null);
      setObjects([]);
      clearHistory();
      setSelectedIds([]);
      setLoadingImage(false);
      return;
    }

    setLoadingImage(true);
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // Hydrate initial annotations if provided (CompanyCam Layer Persistence)
      let rawObjs = [];
      let savedBaseW = null;
      let savedBaseH = null;
      let savedAdj = null;
      let savedRot = 0;

      const incoming = initialAnnotations || initialObjects;
      if (incoming) {
        if (Array.isArray(incoming)) {
          rawObjs = incoming;
        } else if (typeof incoming === "object") {
          rawObjs = incoming.objects || [];
          savedBaseW = incoming.baseWidth;
          savedBaseH = incoming.baseHeight;
          savedAdj = incoming.adjustments;
          savedRot = incoming.rotation || incoming.rotationAngle || 0;
        }
      }

      const viewportW = window.innerWidth || 1200;
      const viewportH = window.innerHeight || 800;
      const maxW = Math.max(700, viewportW - 180);
      const maxH = Math.max(520, viewportH - 150);
      const r = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
      const w = Math.round(img.naturalWidth * r);
      const h = Math.round(img.naturalHeight * r);

      setCurrentFileName(nextFileName || "edited-image.png");
      setImgSize({ w, h });
      setImgEl(img);
      clearHistory();
      setSelectedIds([]);

      if (rawObjs && rawObjs.length > 0) {
        const fromW = savedBaseW || w;
        const fromH = savedBaseH || h;
        const scaled = scaleAnnotationObjects(rawObjs, fromW, fromH, w, h);

        // Re-attach images for stickers
        scaled.forEach((o) => {
          if (o.type === "sticker") {
            const stickerUrl = o.imgSrc || STICKERS.find((s) => s.id === o.sid)?.url;
            if (stickerUrl) {
              const sImg = new window.Image();
              sImg.crossOrigin = "anonymous";
              sImg.onload = () => {
                o.img = sImg;
                if (isMountedRef.current) {
                  setObjects((prev) => [...prev]);
                }
              };
              sImg.src = stickerUrl;
            }
          }
        });

        setObjects(scaled);
        const maxId = scaled.reduce((max, o) => Math.max(max, Number(o.id) || 0), 0);
        idRef.current = maxId;
      } else {
        setObjects([]);
      }

      if (savedAdj) {
        setAdjustments(savedAdj);
      } else if (initialAdjustments) {
        setAdjustments(initialAdjustments);
      } else {
        setAdjustments({ brightness: 100, contrast: 100, saturation: 100 });
      }

      if (canvasRef.current) {
        canvasRef.current.width = w;
        canvasRef.current.height = h;
      }
      setLoadingImage(false);
    };
    img.onerror = () => {
      setLoadingImage(false);
    };
    img.src = src;
  }, [fileName, clearHistory, initialAnnotations, initialObjects, initialAdjustments]);

  // drawing
  const drawing = useRef(false);
  const startPt = useRef({ x: 0, y: 0 });
  const dragInfo = useRef(null); // { objId, handle, startPt, snapObj } or { objId:null, handle:"group-move"|"group-rotate", snapObjs }
  const [angleState, setAngleState] = useState(null); // { id, step: 1|2 } for angle tool multi-click

  // holds the *currently attached* window-level move/up handlers for an in-progress
  // drag (rotate/move/resize/crop). Attaching to window (not just the canvas) means
  // the drag keeps tracking the cursor even after it leaves the canvas element —
  // this is what was causing rotate (and other drags) to "stick"/stop when the
  // cursor briefly left the canvas bounds mid-gesture.
  const activeDrag = useRef(null);
  function attachGlobalDrag(moveFn, upFn) {
    detachGlobalDrag(); // safety: never stack listeners
    const move = (ev) => moveFn(ev);
    const up = (ev) => upFn(ev);
    activeDrag.current = { move, up };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }
  function detachGlobalDrag() {
    if (!activeDrag.current) return;
    const { move, up } = activeDrag.current;
    window.removeEventListener("mousemove", move);
    window.removeEventListener("mouseup", up);
    activeDrag.current = null;
  }
  useEffect(() => detachGlobalDrag, []); // safety net: clean up if unmounted mid-drag

  const {
    cropCanvasRef,
    showCrop,
    setShowCrop,
    openCrop,
    resetCrop,
    doneCrop,
    cancelCrop,
    onCropDown,
    onCropMove,
    onCropUp,
  } = useCropTool({
    imgEl,
    imgSize,
    adjustments,
    canvasRef,
    setImgEl,
    setImgSize,
    setObjects,
    setSelectedIds,
    setAdjustments,
    clearHistory,
    setTool,
    attachGlobalDrag,
    detachGlobalDrag,
  });

  useEffect(() => {
    loadImageSource(source, fileName);
  }, [source, fileName, loadImageSource]);

  // ── Render ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (showGridOverlay) {
      ctx.save();
      ctx.strokeStyle = "rgba(10, 73, 123, 0.38)";
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);

      const spacing = 50;
      for (let x = spacing; x < imgSize.w; x += spacing) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, imgSize.h);
        ctx.stroke();
      }
      for (let y = spacing; y < imgSize.h; y += spacing) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(imgSize.w, y);
        ctx.stroke();
      }
      ctx.restore();
    }

    // draw every object exactly once, then overlay selection UI on top —
    // avoids double-stroking the selected shape (which showed as a subtly
    // heavier/darker outline while dragging).
    const renderOptions = { imgEl, imgSize, adjustments, objects };
    objects.forEach(obj => renderObj(ctx, obj, selectedIds.includes(obj.id), renderOptions));
    if (selectedIds.length === 1) {
      const sel = objects.find(o => o.id === selectedIds[0]);
      if (sel) drawObjectSelection(ctx, sel);
    } else if (selectedIds.length > 1) {
      const sels = objects.filter(o => selectedIds.includes(o.id));
      drawMultiSelection(ctx, sels);
    }

    if (activeMagnifier) {
      const sel = objects.find(o => o.id === activeMagnifier.objId);
      if (sel) {
        const targetPt = localToWorld(sel, handleTargetPoint(sel, activeMagnifier.handle));
        const radius = 70;
        const lensPos = getLensPosition(targetPt.x, targetPt.y, radius);

        drawMagnifierLens(ctx, {
          targetX: targetPt.x,
          targetY: targetPt.y,
          lensX: lensPos.x,
          lensY: lensPos.y,
          anchorX: targetPt.x,
          anchorY: targetPt.y,
          lensRadius: radius,
          zoomLevel: 3,
          color,
          thickness,
          imgEl,
          imgSize,
          adjustments,
          objects,
          selectedIds,
        });
      }
    }
  }, [objects, selectedIds, tool, imgEl, imgSize, adjustments, activeMagnifier, color, thickness, showGridOverlay]);

  // close the style/text popup whenever the selection itself changes
  useEffect(() => { setShowSelEditor(false); }, [selectedIds.join(",")]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    function onKeyDown(e) {
      const tag = document.activeElement?.tagName;
      const typing = tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable;
      if (typing) return;
      const mod = e.ctrlKey || e.metaKey;
      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedIds.length) { e.preventDefault(); deleteSelected(); }
      } else if (mod && e.shiftKey && (e.key === "z" || e.key === "Z")) {
        e.preventDefault(); redo();
      } else if (mod && (e.key === "z" || e.key === "Z")) {
        e.preventDefault(); undo();
      } else if (mod && (e.key === "d" || e.key === "D")) {
        if (selectedIds.length) { e.preventDefault(); duplicateObjects(selectedIds); }
      } else if (mod && (e.key === "c" || e.key === "C")) {
        if (selectedIds.length) { e.preventDefault(); clipboardCopy(); }
      } else if (mod && (e.key === "v" || e.key === "V")) {
        e.preventDefault(); clipboardPaste();
      } else if (e.key === "Escape") {
        setSelectedIds([]); setShowSelEditor(false); closeAllPopups(); setShowTextModal(false); setShowHelpModal(false); setAngleState(null); if (showCrop) { setShowCrop(false); setTool("cursor"); }
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedIds, objects, history, redoStack, showCrop]);

  // opens the floating T-icon editor. It patches the object's properties
  // directly (never recreates it), so the change is saved live with no flicker.
  // Text / Timestamp objects reopen the exact same "Add Text" modal used to
  // create them (pre-filled), matching CompanyCam. Shapes get the small
  // stroke/fill style popup instead.
  function editSelected() {
    if (selectedIds.length !== 1) return;
    const obj = objects.find(o => o.id === selectedIds[0]);
    if (!obj) return;
    if (obj.type === "text" || obj.type === "timestamp") {
      setEditingObjId(obj.id);
      setTextMode(obj.textMode || "abc");
      setTextVal(obj.labelAbc !== undefined ? obj.labelAbc : (obj.text ?? (obj.lines ? obj.lines.join(" ") : "")));
      setTextFt(obj.labelFt || "");
      setTextIn(obj.labelIn || "");
      setTextM(obj.labelM || "");
      setTextCm(obj.labelCm || "");
      setTextHighlight(!!obj.highlight);
      setTextHighlightColor(obj.highlightColor || "#ffff00");
      setPendingTextPos(null);
      setShowTextModal(true);
    } else {
      pushHist(objects); // one undo step covers everything edited while the popup is open
      setShowSelEditor(true);
    }
  }
  function updateSelectedObj(id, patch) {
    setObjects(os => os.map(o => o.id === id ? { ...o, ...patch } : o));
  }
  // Cancel reverts the whole style-edit session in one step, since editSelected
  // pushed a single history snapshot right before opening the popup.
  function cancelStyleEdit() { undo(); setShowSelEditor(false); }
  // Save just closes the popup — edits were already applied live via onUpdate.
  function doneStyleEdit() { setShowSelEditor(false); }

  function onCanvasDown(e) {
    e.preventDefault();
    const pt = getPos(e, canvasRef.current);
    closeAllPopups();
    // mouse-only: touch already keeps firing on its origin element even when the
    // finger leaves it, but mouse events don't — so we track mouse drags on the
    // window for the whole gesture instead of just the canvas.
    if (!e.touches) attachGlobalDrag(onCanvasMove, onCanvasUp);

    // cursor mode
    if (tool === "cursor") {
      // shift-click toggles membership in the multi-selection
      if (e.shiftKey) {
        for (let i = objects.length - 1; i >= 0; i--) {
          if (hitObject(objects[i], pt)) {
            const id = objects[i].id;
            setSelectedIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
            setShowSelEditor(false);
            return;
          }
        }
        return;
      }

      if (selectedIds.length > 1) {
        const sels = objects.filter(o => selectedIds.includes(o.id));
        const b = getMultiBounds(sels);
        if (b) {
          const cx = b.x + b.w / 2;
          if (dist(pt, { x: cx, y: b.y - 32 }) < 12) {
            pushHist(objects);
            dragInfo.current = { objId: null, handle: "group-rotate", startPt: pt, snapObjs: sels.map(cloneObj) };
            return;
          }
          if (pt.x >= b.x - 6 && pt.x <= b.x + b.w + 6 && pt.y >= b.y - 6 && pt.y <= b.y + b.h + 6) {
            pushHist(objects);
            dragInfo.current = { objId: null, handle: "group-move", startPt: pt, snapObjs: sels.map(cloneObj) };
            return;
          }
        }
      } else if (selectedIds.length === 1) {
        const sel = objects.find(o => o.id === selectedIds[0]);
        if (sel) {
          const handle = hitHandle(sel, pt);
          if (handle) {
            pushHist(objects);
            dragInfo.current = { objId: sel.id, handle, startPt: pt, snapObj: cloneObj(sel) };
            // Start magnifier preview timer only for resize/endpoint handles
            const isResizeHandle = ["p1", "p2", "tl", "tr", "bl", "br"].includes(handle);
            const needsMagnifier = isResizeHandle && !["text", "timestamp", "sticker"].includes(sel.type);
            if (needsMagnifier) {
              magnifierTimerRef.current = setTimeout(() => {
                setActiveMagnifier({ objId: sel.id, handle });
              }, 150);
            }
            return;
          }
        }
      }

      for (let i = objects.length - 1; i >= 0; i--) {
        if (hitObject(objects[i], pt)) {
          setSelectedIds([objects[i].id]);
          setShowSelEditor(false);
          pushHist(objects);
          dragInfo.current = { objId: objects[i].id, handle: "move", startPt: pt, snapObj: cloneObj(objects[i]) };
          return;
        }
      }
      setSelectedIds([]); setShowSelEditor(false); return;
    }

    if (tool === "text") { setPendingTextPos(pt); setEditingObjId(null); setTextVal(""); setShowTextModal(true); return; }
    if (tool === "timestamp") {
      const now = new Date();
      const ds = now.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      const ts = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).toLowerCase();
      const obj = { id: newId(), type: "timestamp", x: pt.x, y: pt.y + 32, color, thickness, fontSize: 30, rotation: 0, lines: [ds, ts] };
      pushHist(objects); setObjects(os => [...os, obj]); setSelectedIds([obj.id]); setTool("cursor"); return;
    }
    if (tool === "sticker") return;

    if (tool === "angle") {
      if (!angleState) {
        // First click: set vertex
        pushHist(objects);
        const obj = { id: newId(), type: "angle", color, thickness, rotation: 0, vx: pt.x, vy: pt.y, ax: pt.x, ay: pt.y, bx: pt.x, by: pt.y };
        setObjects(os => [...os, obj]);
        setSelectedIds([obj.id]);
        setAngleState({ id: obj.id, step: 1 });
      } else if (angleState.step === 1) {
        // Second click: set arm A end
        setObjects(os => os.map(o => o.id === angleState.id ? { ...o, ax: pt.x, ay: pt.y } : o));
        setAngleState({ id: angleState.id, step: 2 });
      } else if (angleState.step === 2) {
        // Third click: set arm B end, finalize
        setObjects(os => os.map(o => o.id === angleState.id ? { ...o, bx: pt.x, by: pt.y } : o));
        setAngleState(null);
        setTool("cursor");
      }
      return;
    }


    // shape/pen tools
    drawing.current = true; startPt.current = pt;
    setHasDrawn(true);
    pushHist(objects);
    if (tool === "pen") {
      const obj = { id: newId(), type: "pen", color, thickness, rotation: 0, points: [pt] };
      setObjects(os => [...os, obj]); setSelectedIds([obj.id]);
    } else {
      const obj = { id: newId(), type: tool, color, thickness, rotation: 0, x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y };
      setObjects(os => [...os, obj]); setSelectedIds([obj.id]);
    }
  }

  function onCanvasMove(e) {
    e.preventDefault();
    const pt = getPos(e, canvasRef.current);

    if (dragInfo.current) {
      const { objId, handle, startPt: sp, snapObj: sn, snapObjs } = dragInfo.current;

      // Trigger and update magnifying preview coordinate live during handle drag/resize
      const isResizeHandle = ["p1", "p2", "tl", "tr", "bl", "br"].includes(handle);
      if (isResizeHandle && sn && !["text", "timestamp", "sticker"].includes(sn.type)) {
        if (magnifierTimerRef.current) {
          clearTimeout(magnifierTimerRef.current);
          magnifierTimerRef.current = null;
        }
        if (!activeMagnifier || activeMagnifier.objId !== objId || activeMagnifier.handle !== handle) {
          setActiveMagnifier({ objId, handle });
        }
      }

      if (handle === "group-move") {
        const dx = pt.x - sp.x, dy = pt.y - sp.y;
        setObjects(os => os.map(o => {
          const snap = snapObjs.find(s => s.id === o.id);
          if (!snap) return o;
          return {
            ...snap,
            x: (snap.x || 0) + dx, y: (snap.y || 0) + dy,
            x1: (snap.x1 || 0) + dx, y1: (snap.y1 || 0) + dy, x2: (snap.x2 || 0) + dx, y2: (snap.y2 || 0) + dy,
            vx: snap.vx == null ? snap.vx : snap.vx + dx,
            vy: snap.vy == null ? snap.vy : snap.vy + dy,
            ax: snap.ax == null ? snap.ax : snap.ax + dx,
            ay: snap.ay == null ? snap.ay : snap.ay + dy,
            bx: snap.bx == null ? snap.bx : snap.bx + dx,
            by: snap.by == null ? snap.by : snap.by + dy,
            anchorX: snap.anchorX == null ? snap.anchorX : snap.anchorX + dx,
            anchorY: snap.anchorY == null ? snap.anchorY : snap.anchorY + dy,
            targetX: snap.targetX == null ? snap.targetX : snap.targetX + dx,
            targetY: snap.targetY == null ? snap.targetY : snap.targetY + dy,
            lensX: snap.lensX == null ? snap.lensX : snap.lensX + dx,
            lensY: snap.lensY == null ? snap.lensY : snap.lensY + dy,
            points: snap.points?.map(p => ({ x: p.x + dx, y: p.y + dy }))
          };
        }));
        return;
      }

      if (handle === "group-rotate") {
        const b = getMultiBounds(snapObjs); if (!b) return;
        const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
        const startAng = Math.atan2(sp.y - cy, sp.x - cx);
        const curAng = Math.atan2(pt.y - cy, pt.x - cx);
        const delta = (curAng - startAng) * 180 / Math.PI;
        const rad = delta * Math.PI / 180;
        const rotateAround = (p) => {
          const dx = p.x - cx, dy = p.y - cy;
          return { x: cx + dx * Math.cos(rad) - dy * Math.sin(rad), y: cy + dx * Math.sin(rad) + dy * Math.cos(rad) };
        };
        setObjects(os => os.map(o => {
          const snap = snapObjs.find(s => s.id === o.id);
          if (!snap) return o;
          const no = { ...snap, rotation: ((snap.rotation || 0) + delta + 360) % 360 };
          if (snap.x != null) { const r = rotateAround({ x: snap.x, y: snap.y }); no.x = r.x; no.y = r.y; }
          if (snap.x1 != null) {
            const r1 = rotateAround({ x: snap.x1, y: snap.y1 }); no.x1 = r1.x; no.y1 = r1.y;
            const r2 = rotateAround({ x: snap.x2, y: snap.y2 }); no.x2 = r2.x; no.y2 = r2.y;
          }
          if (snap.anchorX != null) {
            const a = rotateAround({ x: snap.anchorX, y: snap.anchorY }); no.anchorX = a.x; no.anchorY = a.y;
            const t = rotateAround({ x: snap.targetX, y: snap.targetY }); no.targetX = t.x; no.targetY = t.y;
            const l = rotateAround({ x: snap.lensX, y: snap.lensY }); no.lensX = l.x; no.lensY = l.y;
          }
          if (snap.points) no.points = snap.points.map(rotateAround);
          return no;
        }));
        return;
      }

      if (handle === "move") {
        const dx = pt.x - sp.x, dy = pt.y - sp.y;
        if (sn.type === "magnifier") {
          const r = sn.lensRadius || 70;
          const minDx = Math.max(-sn.anchorX, -sn.targetX, r - sn.lensX);
          const maxDx = Math.min(imgSize.w - sn.anchorX, imgSize.w - sn.targetX, imgSize.w - r - sn.lensX);
          const minDy = Math.max(-sn.anchorY, -sn.targetY, r - sn.lensY);
          const maxDy = Math.min(imgSize.h - sn.anchorY, imgSize.h - sn.targetY, imgSize.h - r - sn.lensY);
          const cdx = clamp(dx, minDx, maxDx);
          const cdy = clamp(dy, minDy, maxDy);
          setObjects(os => os.map(o => o.id !== objId ? o : {
            ...sn,
            anchorX: sn.anchorX + cdx, anchorY: sn.anchorY + cdy,
            targetX: sn.targetX + cdx, targetY: sn.targetY + cdy,
            lensX: sn.lensX + cdx, lensY: sn.lensY + cdy,
          }));
          return;
        }
        setObjects(os => os.map(o => o.id !== objId ? o : {
          ...sn,
          x: (sn.x || 0) + dx, y: (sn.y || 0) + dy,
          x1: (sn.x1 || 0) + dx, y1: (sn.y1 || 0) + dy, x2: (sn.x2 || 0) + dx, y2: (sn.y2 || 0) + dy,
          vx: sn.vx == null ? sn.vx : sn.vx + dx,
          vy: sn.vy == null ? sn.vy : sn.vy + dy,
          ax: sn.ax == null ? sn.ax : sn.ax + dx,
          ay: sn.ay == null ? sn.ay : sn.ay + dy,
          bx: sn.bx == null ? sn.bx : sn.bx + dx,
          by: sn.by == null ? sn.by : sn.by + dy,
          anchorX: sn.anchorX == null ? sn.anchorX : sn.anchorX + dx,
          anchorY: sn.anchorY == null ? sn.anchorY : sn.anchorY + dy,
          targetX: sn.targetX == null ? sn.targetX : sn.targetX + dx,
          targetY: sn.targetY == null ? sn.targetY : sn.targetY + dy,
          lensX: sn.lensX == null ? sn.lensX : sn.lensX + dx,
          lensY: sn.lensY == null ? sn.lensY : sn.lensY + dy,
          points: sn.points?.map(p => ({ x: p.x + dx, y: p.y + dy }))
        }));
        return;
      }

      if (sn.type === "magnifier") {
        const dx = pt.x - sp.x, dy = pt.y - sp.y;
        setObjects(os => os.map(o => {
          if (o.id !== objId) return o;
          const r = sn.lensRadius || 70;
          if (handle === "magnifier-anchor") return { ...sn, anchorX: clamp(sn.anchorX + dx, 0, imgSize.w), anchorY: clamp(sn.anchorY + dy, 0, imgSize.h) };
          if (handle === "magnifier-target") return { ...sn, targetX: clamp(sn.targetX + dx, 0, imgSize.w), targetY: clamp(sn.targetY + dy, 0, imgSize.h) };
          if (handle === "magnifier-lens") return { ...sn, lensX: clamp(sn.lensX + dx, r, imgSize.w - r), lensY: clamp(sn.lensY + dy, r, imgSize.h - r) };
          if (handle === "magnifier-radius") {
            const nr = clamp(dist(pt, { x: sn.lensX, y: sn.lensY }), 35, Math.max(36, Math.min(imgSize.w, imgSize.h) / 2));
            return { ...sn, lensRadius: nr, lensX: clamp(sn.lensX, nr, imgSize.w - nr), lensY: clamp(sn.lensY, nr, imgSize.h - nr) };
          }
          return o;
        }));
        return;
      }

      // ── Embedded magnifier lens drag ──────────────────────────────────────
      // handles like "p2-lens", "br-lens", "p1-lens-radius", etc.
      const embLensMatch = handle.match(/^([a-z0-9]+)-lens(-radius)?$/);
      if (embLensMatch) {
        const hk = embLensMatch[1];
        const isRadius = !!embLensMatch[2];
        const magKey = HANDLE_TO_MAG_KEY[hk];
        if (magKey && sn[magKey]) {
          const mag = sn[magKey];
          const dx = pt.x - sp.x, dy = pt.y - sp.y;
          setObjects(os => os.map(o => {
            if (o.id !== objId) return o;
            if (isRadius) {
              const nr = clamp(dist(pt, { x: mag.lensX, y: mag.lensY }), 35, Math.max(36, Math.min(imgSize.w, imgSize.h) / 2));
              return { ...sn, [magKey]: { ...mag, lensRadius: nr, lensX: clamp(mag.lensX, nr, imgSize.w - nr), lensY: clamp(mag.lensY, nr, imgSize.h - nr) } };
            }
            const r = mag.lensRadius || 70;
            return { ...sn, [magKey]: { ...mag, lensX: clamp(mag.lensX + dx, r, imgSize.w - r), lensY: clamp(mag.lensY + dy, r, imgSize.h - r) } };
          }));
          return;
        }
      }

      if (handle === "rotate") {
        // delta-based rotation (matches group-rotate): angle change is measured
        // from the drag START point, then added to the shape's own starting
        // rotation. This tracks the cursor smoothly with no jump and allows
        // free, continuous 360°+ spinning in either direction.
        const b = getBounds(sn); if (!b) return;
        const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
        const startAng = Math.atan2(sp.y - cy, sp.x - cx);
        const curAng = Math.atan2(pt.y - cy, pt.x - cx);
        const delta = (curAng - startAng) * 180 / Math.PI;
        const newRot = ((sn.rotation || 0) + delta + 360) % 360;
        setObjects(os => os.map(o => o.id !== objId ? o : { ...o, rotation: newRot }));
        return;
      }

      // resize handles — work in the object's local (unrotated) space so
      // dragging behaves correctly no matter the current rotation angle
      const lp = toLocal(sn, pt), lsp = toLocal(sn, sp);
      const b = getBounds(sn); const cx = b ? b.x + b.w / 2 : 0, cy = b ? b.y + b.h / 2 : 0;

      if (sn.type === "sticker" || sn.type === "timestamp") {
        const b_sn = getBounds(sn);
        if (!b_sn) return;
        const cx = b_sn.x + b_sn.w / 2;
        const cy = b_sn.y + b_sn.h / 2;

        const startDist = dist(sp, { x: cx, y: cy }) || 1;
        const curDist = dist(pt, { x: cx, y: cy });
        const scale = Math.max(0.3, curDist / startDist);

        const startAng = Math.atan2(sp.y - cy, sp.x - cx);
        const curAng = Math.atan2(pt.y - cy, pt.x - cx);
        const delta = (curAng - startAng) * 180 / Math.PI;
        const newRot = sn.type === "text" ? ((sn.rotation || 0) + delta + 360) % 360 : (sn.rotation || 0);

        setObjects(os => os.map(o => {
          if (o.id !== objId) return o;
          if (o.type === "sticker") {
            return {
              ...o,
              stickerSize: Math.max(12, (sn.stickerSize || 36) * scale),
              rotation: newRot
            };
          }
          if (o.type === "text" || o.type === "timestamp") {
            const fs_new = Math.max(10, (sn.fontSize || 22) * scale);
            const lines = sn.lines || [sn.text];
            const w_new = Math.max(...lines.map(l => l.length)) * fs_new * 0.62 + 8;
            const h_new = lines.length * (fs_new + 4) + 8;
            const x_new = cx + 4 - w_new / 2;
            const y_new = cy + fs_new + 4 - h_new / 2;
            return {
              ...o,
              fontSize: fs_new,
              rotation: newRot,
              x: x_new,
              y: y_new
            };
          }
          return o;
        }));
        return;
      }
      if (sn.type === "pen") {
        const startDist = dist(lsp, { x: cx, y: cy }) || 1;
        const curDist = dist(lp, { x: cx, y: cy });
        const scale = Math.max(0.2, curDist / startDist);
        const newPoints = sn.points.map(p => ({ x: cx + (p.x - cx) * scale, y: cy + (p.y - cy) * scale }));
        setObjects(os => os.map(o => o.id !== objId ? o : { ...o, points: newPoints }));
        return;
      }

      if (sn.type === "angle") {
        const cdx = pt.x - sp.x;
        const cdy = pt.y - sp.y;
        setObjects(os => os.map(o => {
          if (o.id !== objId) return o;
          if (handle === "ax") return { ...sn, ax: sn.ax + cdx, ay: sn.ay + cdy };
          if (handle === "vx") return { ...sn, vx: sn.vx + cdx, vy: sn.vy + cdy };
          if (handle === "bx") return { ...sn, bx: sn.bx + cdx, by: sn.by + cdy };
          return o;
        }));
        return;
      }

      const dx = lp.x - lsp.x, dy = lp.y - lsp.y;
      const dx_w = pt.x - sp.x, dy_w = pt.y - sp.y;
      setObjects(os => os.map(o => {
        if (o.id !== objId) return o;
        const useWorld = o.type === "text";
        const cdx = useWorld ? dx_w : dx;
        const cdy = useWorld ? dy_w : dy;
        if (handle === "p1") return { ...sn, x1: sn.x1 + cdx, y1: sn.y1 + cdy };
        if (handle === "p2") return { ...sn, x2: sn.x2 + cdx, y2: sn.y2 + cdy };
        if (handle === "tl") return { ...sn, x1: sn.x1 + cdx, y1: sn.y1 + cdy };
        if (handle === "tr") return { ...sn, x2: sn.x2 + cdx, y1: sn.y1 + cdy };
        if (handle === "bl") return { ...sn, x1: sn.x1 + cdx, y2: sn.y2 + cdy };
        if (handle === "br") return { ...sn, x2: sn.x2 + cdx, y2: sn.y2 + cdy };
        return o;
      }));
      return;
    }

    // Live preview for angle tool arms
    if (tool === "angle" && angleState) {
      const aid = angleState.id;
      const step = angleState.step;
      setObjects(os => os.map(o => {
        if (o.id !== aid) return o;
        if (step === 1) return { ...o, ax: pt.x, ay: pt.y };
        if (step === 2) return { ...o, bx: pt.x, by: pt.y };
        return o;
      }));
      return;
    }

    if (!drawing.current) return;
    if (tool === "pen") {
      setObjects(os => {
        const last = os[os.length - 1]; if (!last) return os;
        return [...os.slice(0, -1), { ...last, points: [...last.points, pt] }];
      });
    } else if (["arrow", "line", "circle", "rect", "blur", "spotlight"].includes(tool)) {
      setObjects(os => {
        const last = os[os.length - 1]; if (!last) return os;
        return [...os.slice(0, -1), { ...last, x2: pt.x, y2: pt.y }];
      });
    }
  }

  function onCanvasUp(e) {
    if (magnifierTimerRef.current) {
      clearTimeout(magnifierTimerRef.current);
      magnifierTimerRef.current = null;
    }
    setActiveMagnifier(null);

    // once a shape/freehand draw finishes, we USED to drop into select mode automatically.
    // Now, we stay in the drawing tool to allow continuous drawing until the user clicks "Done".
    // if (drawing.current && tool !== "cursor") setTool("cursor");
    dragInfo.current = null;
    drawing.current = false;
    detachGlobalDrag();
  }

  // ── Text commit ────────────────────────────────────────────────────────────
  function commitText() {
    let text = "";
    if (textMode === "abc") text = textVal.trim();
    else if (textMode === "ftin") text = `${textFt} ft. ${textIn} in.`;
    else text = `${textM} m. ${textCm} cm.`;
    if (!text) { setShowTextModal(false); return; }

    if (editingObjId != null) {
      pushHist(objects);
      setObjects(os => os.map(o => {
        if (o.id !== editingObjId) return o;

        if (o.type === "timestamp") {
          return { ...o, text, lines: undefined };
        }

        const rawLines = text.split("\n");
        const maxLineChars = Math.max(...rawLines.map(l => l.length), 1);
        const dx = o.x2 - o.x1;
        const dy = o.y2 - o.y1;
        const len = Math.hypot(dx, dy) || 1;
        const fs = o.fontSize || Math.max(10, Math.min(200, len / (maxLineChars * 0.55))) || 22;
        const w_new = maxLineChars * fs * 0.55;
        const ux = dx / len;
        const uy = dy / len;
        const hl = o.highlight !== undefined ? o.highlight : textHighlight;
        const hlc = o.highlightColor || textHighlightColor || "#ffff00";
        const tc = o.color || color || (hl ? "#000000" : "#ffffff");
        return {
          ...o,
          text,
          lines: rawLines,
          textMode,
          highlight: hl,
          highlightColor: hlc,
          color: tc,
          labelAbc: textVal,
          labelFt: textFt,
          labelIn: textIn,
          labelM: textM,
          labelCm: textCm,
          x2: o.x1 + ux * w_new,
          y2: o.y1 + uy * w_new
        };
      }));
    } else if (pendingTextPos) {
      const fs = textFontSize;
      const rawLines = text.split("\n");
      const maxLineChars = Math.max(...rawLines.map(l => l.length), 1);
      const w = maxLineChars * fs * 0.55;
      const obj = {
        id: newId(),
        type: "text",
        color: color || (textHighlight ? "#000000" : "#ffffff"),
        thickness,
        fontSize: textFontSize,
        bold: textBold,
        align: textAlign,
        highlight: textHighlight,
        highlightColor: textHighlightColor || "#ffff00",
        x1: pendingTextPos.x,
        y1: pendingTextPos.y,
        x2: pendingTextPos.x + w,
        y2: pendingTextPos.y,
        text,
        lines: rawLines,
        textMode,
        labelAbc: textVal,
        labelFt: textFt,
        labelIn: textIn,
        labelM: textM,
        labelCm: textCm
      };
      pushHist(objects); setObjects(os => [...os, obj]); setSelectedIds([obj.id]); setTool("cursor");
    }
    setShowTextModal(false); setTextVal(""); setEditingObjId(null);
  }

  // ── Sticker ────────────────────────────────────────────────────────────────
  function placeSticker(sid) {
    const stickerObj = STICKERS.find(s => s.id === sid);
    if (!stickerObj) return;



    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const obj = { id: newId(), type: "sticker", sid, img, imgSrc: stickerObj.url, x: imgSize.w / 2, y: imgSize.h / 2, color, rotation: 0, stickerSize: 40 };
      pushHist(objects); setObjects(os => [...os, obj]); setSelectedIds([obj.id]);
      setShowStickerPanel(false); setTool("cursor");
    };
    img.src = stickerObj.url;
  }
  function placeCustomSticker(e) {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new window.Image();
      img.onload = () => {
        const obj = { id: newId(), type: "sticker", sid: "custom", img, imgSrc: ev.target.result, x: imgSize.w / 2, y: imgSize.h / 2, color, rotation: 0, stickerSize: 50 };
        pushHist(objects); setObjects(os => [...os, obj]); setSelectedIds([obj.id]);
        setShowStickerPanel(false); setTool("cursor");
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  // ── Rotate image (bakes rotation + current annotations into new base image) ─
  function rotateImage() {
    if (!imgEl) return;
    const oldW = imgSize.w, oldH = imgSize.h;
    
    // Rotate the actual high-res image
    const rawW = imgEl.naturalWidth, rawH = imgEl.naturalHeight;
    const off = document.createElement("canvas");
    off.width = rawH; off.height = rawW;
    const ctx = off.getContext("2d");
    ctx.save();
    ctx.translate(rawH / 2, rawW / 2);
    ctx.rotate(Math.PI / 2);
    ctx.filter = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%)`;
    ctx.drawImage(imgEl, -rawW / 2, -rawH / 2, rawW, rawH);
    ctx.restore();
    ctx.filter = "none";

    // Recalculate optimal logical size for the rotated image (just like initEditor)
    const viewportW = window.innerWidth || 1200;
    const viewportH = window.innerHeight || 800;
    const maxW = Math.max(700, viewportW - 180);
    const maxH = Math.max(520, viewportH - 150);
    // Note: off.width = rawH, off.height = rawW
    const r = Math.min(maxW / rawH, maxH / rawW, 1);
    const newW = Math.round(rawH * r);
    const newH = Math.round(rawW * r);
    
    // Scale factor to map from old rotated dimensions to new maximized dimensions
    const scaleFactor = newW / oldH;

    const rotPt = (p) => ({ x: oldH - p.y, y: p.x });
    const scalePt = (p) => ({ x: p.x * scaleFactor, y: p.y * scaleFactor });

    const rotatedObjects = objects.map(o => {
      const no = { ...o, rotation: ((o.rotation || 0) + 90) % 360 };
      if (no.x != null && no.y != null) { const s = scalePt(rotPt({ x: no.x, y: no.y })); no.x = s.x; no.y = s.y; }
      if (no.x1 != null) { const s1 = scalePt(rotPt({ x: no.x1, y: no.y1 })); no.x1 = s1.x; no.y1 = s1.y; }
      if (no.x2 != null) { const s2 = scalePt(rotPt({ x: no.x2, y: no.y2 })); no.x2 = s2.x; no.y2 = s2.y; }
      if (no.anchorX != null) {
        const a = scalePt(rotPt({ x: no.anchorX, y: no.anchorY })); no.anchorX = a.x; no.anchorY = a.y;
        const t = scalePt(rotPt({ x: no.targetX, y: no.targetY })); no.targetX = t.x; no.targetY = t.y;
        const l = scalePt(rotPt({ x: no.lensX, y: no.lensY })); no.lensX = l.x; no.lensY = l.y;
      }
      if (no.points) no.points = no.points.map(p => scalePt(rotPt(p)));
      
      // Scale object sizes
      if (no.thickness) no.thickness *= scaleFactor;
      if (no.fontSize) no.fontSize *= scaleFactor;
      if (no.lensRadius) no.lensRadius *= scaleFactor;
      
      return no;
    });

    const newImg = new window.Image();
    newImg.onload = () => {
      setImgEl(newImg);
      setImgSize({ w: newW, h: newH });
      setAdjustments({ brightness: 100, contrast: 100, saturation: 100 });
      setObjects(rotatedObjects);
      setSelectedIds([]);
      setSessionRotation(r => (r + 90) % 360);
    };
    newImg.src = off.toDataURL("image/jpeg", 0.95);
  }

  // ── Flatten (shared by Save + Download) ──────────────────────────────────
  // Renders the base photo + every annotation object fresh, directly onto an
  // offscreen canvas via renderObj — it does NOT copy the live on-screen
  // canvas. Copying the live canvas depended on React's async re-render
  // finishing (via requestAnimationFrame) before the copy ran, which could
  // race and silently grab a stale/partial frame (or one still showing
  // selection handles) — that race is what made downloads unreliable.
  function getFlattenedCanvas() {
    const MAX_DIM = 1920;
    let rawW = imgEl ? imgEl.naturalWidth : imgSize.w;
    let rawH = imgEl ? imgEl.naturalHeight : imgSize.h;
    if (rawW > MAX_DIM || rawH > MAX_DIM) {
      if (rawW > rawH) { rawH = Math.round(rawH * (MAX_DIM / rawW)); rawW = MAX_DIM; }
      else { rawW = Math.round(rawW * (MAX_DIM / rawH)); rawH = MAX_DIM; }
    }

    const off = document.createElement("canvas");
    off.width = rawW; 
    off.height = rawH;
    const ctx = off.getContext("2d");

    const scale = rawW / (imgSize.w || 1);
    ctx.scale(scale, scale);

    if (imgEl) {
      ctx.filter = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%)`;
      ctx.drawImage(imgEl, 0, 0, imgSize.w, imgSize.h);
      ctx.filter = "none";
    }
    objects.forEach(obj => renderObj(ctx, obj, false, { imgEl, imgSize, adjustments, objects, isExport: true }));
    return off;
  }

  // ── Clean Base Canvas (Without Annotations) ────────────────────────────────
  // Renders only the transformed base image (rotation, crop, brightness/contrast filters)
  // WITHOUT any overlay annotations. This is saved to S3 original path so re-editing
  // always starts with the correctly rotated/cropped clean photo.
  function getCleanBaseCanvas() {
    const MAX_DIM = 1920;
    let rawW = imgEl ? imgEl.naturalWidth : imgSize.w;
    let rawH = imgEl ? imgEl.naturalHeight : imgSize.h;
    if (rawW > MAX_DIM || rawH > MAX_DIM) {
      if (rawW > rawH) { rawH = Math.round(rawH * (MAX_DIM / rawW)); rawW = MAX_DIM; }
      else { rawW = Math.round(rawW * (MAX_DIM / rawH)); rawH = MAX_DIM; }
    }

    const off = document.createElement("canvas");
    off.width = rawW;
    off.height = rawH;
    const ctx = off.getContext("2d");

    const scale = rawW / (imgSize.w || 1);
    ctx.scale(scale, scale);

    if (imgEl) {
      ctx.filter = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%)`;
      ctx.drawImage(imgEl, 0, 0, imgSize.w, imgSize.h);
      ctx.filter = "none";
    }
    return off;
  }

  // ── Download ───────────────────────────────────────────────────────────────
  // Exports the flattened photo as a PNG file onto the user's device. This is
  // the "download icon" action — it never mutates editor state.
  function downloadImage() {
    setSelectedIds([]);
    setShowSelEditor(false);
    try {
      const off = getFlattenedCanvas();
      const dlName = (currentFileName || "edited-image.png").replace(/\.png$/i, ".jpg");
      const triggerDownload = (href) => {
        const a = document.createElement("a");
        a.download = dlName; a.href = href;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      };

      if (off.toBlob) {
        off.toBlob((blob) => {
          if (!blob) { triggerDownload(off.toDataURL("image/jpeg", 0.92)); return; }
          const url = URL.createObjectURL(blob);
          triggerDownload(url);
          setTimeout(() => URL.revokeObjectURL(url), 2000);
        }, "image/jpeg", 0.92);
      } else {
        triggerDownload(off.toDataURL("image/jpeg", 0.92));
      }
    } catch (err) {
      console.error("Download failed:", err);
      alert("Sorry, the download failed. Please try again.");
    }
  }

  // ── Save ───────────────────────────────────────────────────────────────────
  // The "Save" button persists the edit instead of pushing a file to disk:
  //  - if the host app passed an onSave callback, hand it the flattened PNG
  //    (as a data URL) so it can upload/store it wherever it needs to.
  //  - either way, the annotations get baked into the base photo (same bake
  //    pattern as rotate/crop) so the canvas is cleared and further edits
  //    start from a clean, saved slate. A brief "Saved" toast confirms it.
  function saveImage() {
    if (!imgEl) return;
    setSelectedIds([]);
    setShowSelEditor(false);
    setSaveStatus("saving");

    setTimeout(() => {
      try {
        const off = getFlattenedCanvas();
        const cleanOff = getCleanBaseCanvas();
        const cleanDataUrl = cleanOff.toDataURL("image/jpeg", 0.95);
        const saveName = (currentFileName || "edited-image.png").replace(/\.png$/i, ".jpg");
        const serializableObjects = objects.map((o) => {
          const copy = { ...o };
          delete copy.img;
          return copy;
        });
        const annotationsPayload = {
          baseWidth: imgSize.w,
          baseHeight: imgSize.h,
          objects: serializableObjects,
          adjustments: { ...adjustments },
          rotationAngle: (savedRotRef.current + sessionRotation) % 360
        };

        const finish = async (dataUrl) => {
          if (typeof onSave === "function") {
            try {
              await onSave({
                imageBase64: dataUrl,
                cleanBase64: cleanDataUrl,
                fullName: saveName,
                mimeType: "image/jpeg",
                annotations: annotationsPayload,
              });
            } catch (err) {
              console.error("onSave handler failed:", err);
              if (isMountedRef.current) {
                setSaveStatus("fail");
                setTimeout(() => { if (isMountedRef.current) setSaveStatus(null); }, 3000);
              }
              return;
            }
          }
          if (isMountedRef.current) {
            setSaveStatus("saved");
            setTimeout(() => { if (isMountedRef.current) setSaveStatus(null); }, 1000);
          }
        };

        if (off.toBlob) {
          off.toBlob((blob) => {
            if (!blob) { finish(off.toDataURL("image/jpeg", 0.92)); return; }
            const reader = new FileReader();
            reader.onload = () => finish(reader.result);
            reader.readAsDataURL(blob);
          }, "image/jpeg", 0.92);
        } else {
          finish(off.toDataURL("image/jpeg", 0.92));
        }
      } catch (err) {
        console.error("Save failed:", err);
        setSaveStatus("fail");
        setTimeout(() => setSaveStatus(null), 3000);
        alert("Sorry, saving failed. Please try again.");
      }
    }, 50);
  }

  // ── File load ──────────────────────────────────────────────────────────────
  function handleFile(e) {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => loadImageSource(ev.target.result, file.name || "edited-image.png");
    reader.readAsDataURL(file);
  }

  function closeAllPopups() { setShowColorPopup(false); setShowThickPopup(false); setShowAdjust(false); setShowStickerPanel(false); }

  function handleToolSelect(newTool) {
    setHasDrawn(false);
    const autoTools = ["line", "arrow", "circle", "rect", "blur", "spotlight", "text", "timestamp"];
    if (autoTools.includes(newTool) && imgSize.w) {
      const cx = imgSize.w / 2;
      const cy = imgSize.h / 2;
      let obj = null;
      if (newTool === "text") {
        setPendingTextPos({ x: cx, y: cy });
        setEditingObjId(null);
        setTextVal("");
        setShowTextModal(true);
      } else if (newTool === "timestamp") {
        const now = new Date();
        const ds = now.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        const ts = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).toLowerCase();
        obj = { id: newId(), type: "timestamp", x: cx, y: cy, color, thickness, fontSize: 30, rotation: 0, lines: [ds, ts] };
      } else if (newTool === "line" || newTool === "arrow") {
        obj = { id: newId(), type: newTool, x1: cx - 100, y1: cy, x2: cx + 100, y2: cy, color, thickness, rotation: 0 };
      } else if (["circle", "rect", "blur", "spotlight"].includes(newTool)) {
        obj = { id: newId(), type: newTool, x1: cx - 80, y1: cy - 80, x2: cx + 80, y2: cy + 80, color, thickness, rotation: 0 };
      }

      if (obj) {
        pushHist(objects);
        setObjects(os => [...os, obj]);
        setSelectedIds([obj.id]);
        setHasDrawn(true);
      }
      setTool("cursor");
      return;
    }
    setTool(newTool);
  }

  // ── Fullscreen toggle ──────────────────────────────────────────────────────
  function toggleFullscreen() {
    try {
      if (!document.fullscreenElement) {
        editorRef.current?.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    } catch (err) { /* Fullscreen API may be blocked in this environment */ }
  }
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const cursorStyle = { cursor: tool === "cursor" ? "default" : tool === "text" ? "text" : "crosshair" };

  return (
    <div ref={editorRef} className="fd-editor-root" style={{ position: "fixed", inset: 0, zIndex: 9999, background: "#0a0a0c", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        @keyframes editor-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fd-fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fd-popIn {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }

        /* Sleek custom scrollbars for easy navigation */
        .fd-vscroll::-webkit-scrollbar {
          width: 4px;
        }
        .fd-vscroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .fd-vscroll::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.15);
          border-radius: 4px;
        }
        .fd-vscroll::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
        .fd-vscroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
        }

        .fd-hscroll::-webkit-scrollbar {
          height: 4px;
        }
        .fd-hscroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .fd-hscroll::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.15);
          border-radius: 4px;
        }
        .fd-hscroll::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
        .fd-hscroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
        }



        /* Range input styling */
        input[type='range'].fd-dark-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          border-radius: 2px;
          background: rgba(255,255,255,0.12);
          outline: none;
        }
        input[type='range'].fd-dark-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 14px; height: 14px;
          border-radius: 50%;
          background: #ffcc00;
          cursor: pointer;
          box-shadow: 0 1px 6px rgba(0,0,0,0.3);
        }

        /* Responsive variables */
        .fd-editor-root {
          --fd-chrome-size: 68px;
        }
        @media (max-width: 600px) {
          .fd-editor-root {
            --fd-chrome-size: 56px;
          }
        }
        @media (max-width: 1023px) {
          .fd-sidebar-vertical {
            top: var(--fd-chrome-size) !important;
          }
        }

        /* Responsive canvas area margins for perfect centering */
        .fd-canvas-area { position: absolute !important; top: 0; bottom: 0; right: 0; left: var(--fd-chrome-size); margin: 0; }
        @media (max-width: 1023px) {
          .fd-canvas-area { left: 0; bottom: var(--fd-chrome-size); right: 0; top: 0; margin: 0; }
        }
        .fd-canvas-area.force-bottom {
          left: 0 !important; bottom: var(--fd-chrome-size) !important; right: 0 !important; top: 0 !important; margin: 0 !important;
        }
        .fd-canvas-area.force-left {
          left: var(--fd-chrome-size) !important; bottom: 0 !important; right: 0 !important; top: 0 !important; margin: 0 !important;
        }
      `}</style>
      {loadingImage && (
        <div style={{
          position: "absolute",
          inset: 0,
          background: "rgba(10, 10, 10, 0.85)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          gap: 16
        }}>
          <div className="editor-spinner" style={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            border: "3px solid rgba(255,255,255,0.1)",
            borderTopColor: "#ffcc00",
            animation: "editor-spin 0.8s linear infinite"
          }} />
          <span style={{ color: "#fff", fontSize: 14, fontWeight: 500, letterSpacing: "0.5px" }}>Loading Image...</span>
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />

      <EditorToolChrome
        fileRef={fileRef}
        customStickerRef={customStickerRef}
        tool={tool}
        color={color}
        thickness={thickness}
        showColorPopup={showColorPopup}
        showThickPopup={showThickPopup}
        showStickerPanel={showStickerPanel}
        showAdjust={showAdjust}
        showCrop={showCrop}
        hasDrawn={hasDrawn}
        history={history}
        redoStack={redoStack}
        objects={objects}
        selectedIds={selectedIds}
        adjustments={adjustments}
        setTool={handleToolSelect}
        setColor={setColor}
        setThickness={setThickness}
        setShowColorPopup={setShowColorPopup}
        setShowThickPopup={setShowThickPopup}
        setShowStickerPanel={setShowStickerPanel}
        setShowAdjust={setShowAdjust}
        setObjects={setObjects}
        setSelectedIds={setSelectedIds}
        setAdjustments={setAdjustments}
        closeAllPopups={closeAllPopups}
        rotateImage={rotateImage}
        openCrop={openCrop}
        resetCrop={resetCrop}
        doneCrop={doneCrop}
        cancelCrop={cancelCrop}
        undo={undo}
        redo={redo}
        pushHist={pushHist}
        placeSticker={placeSticker}
        placeCustomSticker={placeCustomSticker}
        showGridOverlay={showGridOverlay}
        setShowGridOverlay={setShowGridOverlay}
        layoutMode={layoutMode}
      />
      
      {/* ── Top Bar (Moved out of canvas so it doesn't get clipped) ── */}
      <EditorTopBar
        tool={tool}
        zoom={zoom}
        isFullscreen={isFullscreen}
        saveStatus={saveStatus}
        onClose={handleCloseRequest}
        setTool={handleToolSelect}
        setZoom={setZoom}
        closeAllPopups={closeAllPopups}
        toggleFullscreen={toggleFullscreen}
        downloadImage={downloadImage}
        saveImage={saveImage}
        setShowHelpModal={setShowHelpModal}
        undo={undo}
        redo={redo}
        history={history}
        redoStack={redoStack}
        clearObjects={() => {
          setShowClearConfirm(true);
        }}
        layoutMode={layoutMode}
        setLayoutMode={setLayoutMode}
      />

      {/* ── Main Content Area ──────────────────────────────────────────────── */}
      <div className={`fd-canvas-area ${layoutMode === "bottom" ? "force-bottom" : layoutMode === "left" ? "force-left" : ""}`} style={{ display: "flex", flexDirection: "column", overflow: "hidden", background: "#0a0a0c" }}>
        {/* Angle tool instruction banner */}
        {tool === "angle" && (
          <div style={{
            position: "absolute",
            top: 56,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(15, 15, 18, 0.94)",
            border: "1px solid rgba(255,204,0,0.3)",
            borderRadius: 12,
            padding: "8px 16px",
            color: "#fff",
            fontSize: 12,
            fontWeight: 500,
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            zIndex: 35,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            gap: 10,
            animation: "fd-fadeIn 0.2s ease",
            maxWidth: "90%",
          }}>
            <span style={{ color: "#ffcc00", fontSize: 15, flexShrink: 0 }}>📐</span>
            <span>
              {!angleState ? (
                "Step 1: Click the CORNER (Vertex) of the angle"
              ) : angleState.step === 1 ? (
                "Step 2: Click to set the FIRST arm endpoint"
              ) : (
                "Step 3: Click to set the SECOND arm endpoint"
              )}
            </span>
          </div>
        )}

        {/* Canvas wrapper — checkerboard background for transparent images */}
        <div 
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            background: "radial-gradient(circle at 50% 50%, #141418 0%, #0a0a0c 100%)",
          }} 
          onClick={() => { if (!showCrop) closeAllPopups(); }}
          onWheel={(e) => {
            // Adjust zoom level based on scroll
            const delta = e.deltaY * -0.0015;
            setZoom(z => {
              let newZoom = z + delta;
              if (newZoom < 0.2) newZoom = 0.2;
              if (newZoom > 5) newZoom = 5;
              // Snap to 100% if close
              if (Math.abs(newZoom - 1) < 0.05) newZoom = 1;
              return newZoom;
            });
          }}
        >
          <div style={{
            position: "relative",
            display: "inline-block",
            transform: `scale(${zoom})`,
            transformOrigin: "center center",
            transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "0 8px 40px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)",
            borderRadius: 3,
          }}>
            {imgEl && (
              <img src={imgEl.src} width={imgSize.w} height={imgSize.h} alt="" draggable={false}
                style={{
                  display: "block",
                  userSelect: "none",
                  pointerEvents: "none",
                  filter: `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%)`,
                  borderRadius: 3,
                }} />
            )}
            {/* Drawing canvas */}
            <canvas ref={canvasRef} width={imgSize.w} height={imgSize.h}
              style={{ position: imgEl ? "absolute" : "relative", top: 0, left: 0, touchAction: "none", borderRadius: 3, ...cursorStyle }}
              onMouseDown={showCrop ? onCropDown : onCanvasDown}
              onTouchStart={showCrop ? onCropDown : onCanvasDown}
              onTouchMove={showCrop ? onCropMove : onCanvasMove}
              onTouchEnd={showCrop ? onCropUp : onCanvasUp}
            />
            {/* Crop overlay canvas */}
            {showCrop && (
              <canvas ref={cropCanvasRef} width={imgSize.w} height={imgSize.h}
                style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", borderRadius: 3 }} />
            )}
            <SelectionOverlay
              objects={objects}
              selectedIds={selectedIds}
              imgSize={imgSize}
              showCrop={showCrop}
              tool={tool}
              showSelEditor={showSelEditor}
              editSelected={editSelected}
              duplicateObjects={duplicateObjects}
              deleteSelected={deleteSelected}
              updateSelectedObj={updateSelectedObj}
              cancelStyleEdit={cancelStyleEdit}
              doneStyleEdit={doneStyleEdit}
            />
          </div>
        </div>
      </div>

      <TextEditModal
        showTextModal={showTextModal}
        editingObj={editingObjId != null ? objects.find(o => o.id === editingObjId) : null}
        isTimestamp={editingObjId != null ? objects.find(o => o.id === editingObjId)?.type === "timestamp" : tool === "timestamp"}
        textMode={textMode}
        textVal={textVal}
        textFt={textFt}
        textIn={textIn}
        textM={textM}
        textCm={textCm}
        setTextMode={setTextMode}
        setTextVal={setTextVal}
        setTextFt={setTextFt}
        setTextIn={setTextIn}
        setTextM={setTextM}
        setTextCm={setTextCm}
        setShowTextModal={setShowTextModal}
        setEditingObjId={setEditingObjId}
        updateSelectedObj={updateSelectedObj}
        commitText={commitText}
        textFontSize={textFontSize} setTextFontSize={setTextFontSize}
        textBold={textBold} setTextBold={setTextBold}
        textAlign={textAlign} setTextAlign={setTextAlign}
        textHighlight={textHighlight} setTextHighlight={setTextHighlight}
        textHighlightColor={textHighlightColor} setTextHighlightColor={setTextHighlightColor}
        color={color} setColor={setColor}
      />

      {showHelpModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowHelpModal(false)}>
          <div style={{
            background: "rgba(22, 22, 28, 0.97)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 20,
            padding: "24px 28px",
            width: 420,
            maxWidth: "92%",
            boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
            color: "#fff",
            animation: "fd-popIn 0.2s ease-out",
          }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, letterSpacing: "0.3px" }}>Keyboard Shortcuts</h2>
              <button onClick={() => setShowHelpModal(false)} style={{
                background: "rgba(255,255,255,0.06)", border: "none", color: "#888",
                cursor: "pointer", fontSize: 14, width: 28, height: 28, borderRadius: 8,
                display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "#fff"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "#888"; }}>
                ✕
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { keys: ["Ctrl", "Z"], desc: "Undo" },
                { keys: ["Ctrl", "Shift", "Z"], desc: "Redo" },
                { keys: ["Delete"], desc: "Delete selected" },
                { keys: ["Ctrl", "D"], desc: "Duplicate selected" },
                { keys: ["Ctrl", "C"], desc: "Copy selected" },
                { keys: ["Ctrl", "V"], desc: "Paste" },
                { keys: ["Esc"], desc: "Deselect / Cancel / Close" },
              ].map(({ keys, desc }, idx) => (
                <div key={idx} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.05)"
                }}>
                  <span style={{ fontSize: 13, color: "#bbb", fontWeight: 500 }}>{desc}</span>
                  <div style={{ display: "flex", gap: 4 }}>
                    {keys.map((k, ki) => (
                      <kbd key={ki} style={{
                        background: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.15)",
                        borderRadius: 6,
                        padding: "4px 10px",
                        fontSize: 11,
                        fontFamily: "'Inter', monospace",
                        fontWeight: 600,
                        color: "#fff",
                        boxShadow: "0 2px 0 rgba(0,0,0,0.4)",
                        letterSpacing: "0.3px",
                      }}>{k}</kbd>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <button onClick={() => setShowHelpModal(false)}
              style={{
                width: "100%", marginTop: 20, padding: "10px 0", borderRadius: 10,
                background: "linear-gradient(135deg, #ffcc00, #f5a623)", color: "#000",
                fontWeight: 700, border: "none", cursor: "pointer", fontSize: 13,
                boxShadow: "0 2px 12px rgba(255,204,0,0.3)", outline: "none",
                letterSpacing: "0.3px", transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(255,204,0,0.45)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(255,204,0,0.3)"; }}>
              Got it
            </button>
          </div>
        </div>
      )}

      {showClearConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowClearConfirm(false)}>
          <div style={{
            background: "rgba(22, 22, 28, 0.97)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 20,
            padding: "24px 28px",
            width: 320,
            maxWidth: "92%",
            boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
            color: "#fff",
            animation: "fd-popIn 0.2s ease-out",
            textAlign: "center"
          }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 12px 0", letterSpacing: "0.3px" }}>Clear All Edits?</h2>
            <p style={{ fontSize: 13, color: "#aaa", margin: "0 0 24px 0", lineHeight: 1.4 }}>
              How do you want to clear your edits? You can permanently clear everything, or clear it but keep it in history (Undoable).
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

              <button onClick={() => {
                if (objects.length > 0) {
                  pushHist(objects);
                  setObjects([]);
                  setSelectedIds([]);
                }
                setAdjustments({ brightness: 100, contrast: 100, saturation: 100 });
                setShowSelEditor(false);
                closeAllPopups();
                setTool("cursor");
                setShowClearConfirm(false);
              }}
                style={{
                  width: "100%", padding: "12px 0", borderRadius: 10,
                  background: "#ffcc00", color: "#000",
                  fontWeight: 700, border: "none", cursor: "pointer", fontSize: 13,
                  boxShadow: "0 2px 12px rgba(255,204,0,0.3)", outline: "none",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}>
                Clear (Keep in History)
              </button>

              <button onClick={() => {
                clearHistory();
                if (objects.length > 0) {
                  setObjects([]);
                  setSelectedIds([]);
                }
                setAdjustments({ brightness: 100, contrast: 100, saturation: 100 });
                setShowSelEditor(false);
                closeAllPopups();
                setTool("cursor");
                setShowClearConfirm(false);
              }}
                style={{
                  width: "100%", padding: "12px 0", borderRadius: 10,
                  background: "#ef4444", color: "#fff",
                  fontWeight: 600, border: "none", cursor: "pointer", fontSize: 13,
                  boxShadow: "0 2px 12px rgba(239, 68, 68, 0.3)", outline: "none",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#dc2626"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#ef4444"; e.currentTarget.style.transform = "translateY(0)"; }}>
                Clear All Permanently
              </button>

              <button onClick={() => setShowClearConfirm(false)}
                style={{
                  width: "100%", padding: "12px 0", borderRadius: 10,
                  background: "rgba(255,255,255,0.1)", color: "#fff",
                  fontWeight: 600, border: "none", cursor: "pointer", fontSize: 13,
                  transition: "all 0.15s", outline: "none", marginTop: 4,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Confirmation Modal - Premium Dark Theme */}
      {showCloseConfirm && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 100000,
          background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}
          onClick={() => setShowCloseConfirm(false)}>
          <div style={{
            background: "#1c1c22",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 16,
            width: 440,
            maxWidth: "92%",
            boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
            color: "#fff",
            animation: "fd-popIn 0.2s ease-out",
            display: "flex", flexDirection: "column",
            overflow: "hidden"
          }}
            onClick={e => e.stopPropagation()}>
            
            <div style={{ padding: "32px 32px 24px 32px", textAlign: "left" }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 10px 0", color: "#fff" }}>Unsaved Changes</h2>
              <p style={{ fontSize: 14, color: "#94a3b8", margin: 0, lineHeight: 1.5 }}>
                You have made edits to this image. Do you want to save them before closing?
              </p>
            </div>
            
            <div style={{ 
              background: "rgba(0,0,0,0.2)",
              padding: "20px 32px", 
              display: "flex", justifyContent: "flex-end", gap: 12,
              borderTop: "1px solid rgba(255,255,255,0.05)"
            }}>
              <button onClick={() => setShowCloseConfirm(false)}
                style={{
                  padding: "9px 19px", borderRadius: 8,
                  background: "transparent", color: "#94a3b8",
                  fontWeight: 600, border: "1px solid rgba(148, 163, 184, 0.4)", cursor: "pointer", fontSize: 13,
                  transition: "all 0.15s", outline: "none", whiteSpace: "nowrap"
                }}
                onMouseEnter={e => { e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "#94a3b8"; e.currentTarget.style.borderColor = "rgba(148, 163, 184, 0.4)"; }}>
                Keep Editing
              </button>

              <button onClick={() => {
                setShowCloseConfirm(false);
                if (typeof onClose === "function") onClose();
              }}
                style={{
                  padding: "10px 20px", borderRadius: 8,
                  background: "#ef4444", color: "#ffffff",
                  fontWeight: 600, border: "none", cursor: "pointer", fontSize: 13,
                  transition: "all 0.15s", outline: "none", whiteSpace: "nowrap",
                  boxShadow: "0 2px 8px rgba(239, 68, 68, 0.2)"
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#dc2626"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#ef4444"; e.currentTarget.style.transform = "translateY(0)"; }}>
                Discard
              </button>

              <button onClick={() => {
                setShowCloseConfirm(false);
                saveImage();
              }}
                style={{
                  padding: "10px 24px", borderRadius: 8,
                  background: "#ffcc00", color: "#000",
                  fontWeight: 700, border: "none", cursor: "pointer", fontSize: 13,
                  boxShadow: "0 4px 14px rgba(255,204,0,0.25)", outline: "none",
                  transition: "all 0.15s", whiteSpace: "nowrap"
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(255,204,0,0.35)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(255,204,0,0.25)"; }}>
                Save & Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
